import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Row,
  Col,
  notification,
  Space,
  Tag,
  Tooltip,
  Card,
  Flex,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { UploadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchLeadDetailsServices } from "../../auth/slice/authSlice";
import TransactionModal from "./TransactionModal";
import Loader from "../../../components/common/Loader";
import {
  deleteLeadFinanceInformationService,
  getLeadFinanceInformationService,
  addLeadFinanceInformationService,
  getLeadFinanceInformationByIdService,
  updateLeadFinanceInformationService,
  getPaymentTransactionsService,
  addPaymentTransactionService,
  updatePaymentTransactionService,
  deletePaymentTransactionService,
} from "../../../services/admin-services";
import {
  getBankerService,
  getFileStagesService,
} from "../../../services/master-services";

const { TextArea } = Input;
const { Title } = Typography;

const Payments = () => {
  const [form] = Form.useForm();
  const [financeForm] = Form.useForm();
  const dispatch = useDispatch();

  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isTransactionSaveLoading, setIsTransactionSaveLoading] = useState(false);
  const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);
  const [isFinanceSaveLoading, setIsFinanceSaveLoading] = useState(false);
  const [isEditFinance, setIsEditFinance] = useState(false);
  const [isEditTransaction, setIsEditTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  
  const [transactionFile, setTransactionFile] = useState([]);
  const [financeFiles, setFinanceFiles] = useState([]);
  const [isDeleteTransactionModalOpen, setIsDeleteTransactionModalOpen] =
    useState(false);
  const [deleteTransactionId, setDeleteTransactionId] = useState(null);
  const [deleteTransactionForm] = Form.useForm();
  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const paymentBy = Form.useWatch("payment_by", financeForm);
  const { id: leadId } = useParams();
  const leadDetails = useSelector((state) => state?.auth?.leadDetails);
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const bookingData = leadDetails?.booking;

  // Check if role is Procurement Manager or Exchange Evaluation Team - they can only view, no actions
  const isViewOnlyRole =
    roleType === "Procurement Manager" ||
    roleType === "Exchange Evaluation Team";

  // Check if lead status is Lost, Converted, or Dropped
  // const isLeadLostOrConverted =
  //   leadDetails?.lead_status?.name === 'Lost' ||
  //   leadDetails?.lead_status?.name === 'Converted' ||
  //   leadDetails?.lead_status?.name === 'Dropped';

  // Check if lead status is Open
  const isLeadOpen = leadDetails?.lead_status?.name === "Open";

  // Check if refund transaction is enabled
  const isRefundEnabled = leadDetails?.refund_transaction_enabled === true;

  // Check if lead status is Refunded or Cancelled
  const isLeadRefundedOrCancelled =
    
    leadDetails?.lead_status?.name === "Cancelled" ||
    leadDetails?.lead_status?.name === "Dropped" ||
    leadDetails?.lead_status?.name === "Lost";

  // Get values from booking data
  const totalPaymentReceived = bookingData?.total_payment_received || 0;
  const cashAmountReceived = bookingData?.cash_amt_received || 0;
  const dealValue = bookingData?.total_customer_payable || 0;
  const refundAmount = bookingData?.refund_amount || 0;
  
  // Calculate Net Customer Balance
  // For Cancelled/Dropped/Lost: Deal Value - (Total Payment Received - Refund Amount)
  const netCusBalance = isLeadRefundedOrCancelled
    ? dealValue - (totalPaymentReceived - refundAmount)
    : bookingData?.net_customer_balance || 0;
  
  const displayCashAmount = cashAmountReceived || 0;
  const isBookingAmountZero = leadDetails?.booking_amount_received === false;

  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeData, setFinanceData] = useState([]);
  const [financeSearch, setFinanceSearch] = useState("");
  const [financePagination, setFinancePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [bankOptions, setBankOptions] = useState([]);
  const [fileStages, setFileStages] = useState([]);
  const [editingFinanceId, setEditingFinanceId] = useState(null);

  // Transactions state
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnData, setTxnData] = useState([]);
  const [txnSearch, setTxnSearch] = useState("");
  const [txnPaymentType, setTxnPaymentType] = useState(undefined);
  const [txnPaymentMode, setTxnPaymentMode] = useState(undefined);
  const [txnPagination, setTxnPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [txnFinalBalance, setTxnFinalBalance] = useState(null);

  const fetchFinanceInformation = async (
    page = financePagination.current,
    perPage = financePagination.pageSize,
    search = financeSearch
  ) => {
    try {
      setFinanceLoading(true);
      const res = await getLeadFinanceInformationService({
        page,
        per_page: perPage,
        search: search || undefined,
        lead_id: leadId,
      });
      const list = res?.data?.data?.list_data || [];
      const total = res?.data?.data?.total_records ?? 0;
      const rows = Array.isArray(list)
        ? list.map((item) => ({
          key: item.id || item.key,
          id: item.id,
          payment_by: item.payment_by,
          financier_name: item.bank_name,
          in_house: item.in_house,
          file_stage: item.file_stage_name,
          documents: item.documents || [],
          remarks: item.remarks,
          last_updated: item.created_at
            ? dayjs(item.created_at).format("DD/MM/YYYY")
            : "-",
          created_by: item.created_by,
        }))
        : [];
      setFinanceData(rows);
      setFinancePagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setFinanceLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchFinanceInformation(1, financePagination.pageSize, financeSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleFinanceSearchSubmit = (value) => {
    const nextSearch = typeof value === "string" ? value : financeSearch;
    setFinanceSearch(nextSearch);
    fetchFinanceInformation(1, financePagination.pageSize, nextSearch?.trim());
  };

  useEffect(() => {
    // Load master data for Finance modal selects
    const loadMasters = async () => {
      try {
        const [banksRes, stagesRes] = await Promise.all([
          getBankerService(true),
          getFileStagesService(),
        ]);
        const banks = banksRes?.data?.data || [];
        const stages = stagesRes?.data?.data || [];
        setBankOptions(banks);
        setFileStages(stages);
      } catch (e) {
        // non-blocking
      }
    };
    loadMasters();
  }, []);

  // Transactions: fetcher
  const fetchTransactions = async (
    page = txnPagination.current,
    perPage = txnPagination.pageSize,
    search = txnSearch,
    paymentType = txnPaymentType,
    paymentMode = txnPaymentMode
  ) => {
    try {
      setTxnLoading(true);
      const res = await getPaymentTransactionsService({
        page,
        per_page: perPage,
        search: search || undefined,
        lead_id: leadId,
        payment_type:
          paymentType === undefined || paymentType === null ? "" : paymentType,
        payment_mode:
          paymentMode === undefined || paymentMode === null ? "" : paymentMode,
      });
      const list = res?.data?.data?.list_data || [];
      const total = res?.data?.data?.total_records ?? 0;
      const rows = Array.isArray(list)
        ? list.map((item) => ({
          key: item.id,
          id: item.id,
          transaction_mode: item.transaction_mode,
          transaction_nature: item.transaction_nature,

          payment_for: item.payment_type,
          amount: item.amount,
          running_balance: item.running_balance,
          payment_mode: item.payment_mode,
          mr_utr_number: item.mr_utr_number,
          paid_on: item.payment_date
            ? dayjs(item.payment_date).format("DD/MM/YYYY")
            : "-",
          attachment: item.file_name,
          attachment_path: item.file_path,
          remarks: item.remarks,
          updated_by: item.created_by,
          delete_restriction: item.delete_restriction,
        }))
        : [];
      setTxnData(rows);
      setTxnPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total,
      }));
      setTxnFinalBalance(res?.data?.data?.final_balance ?? null);
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setTxnLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchTransactions(
        1,
        txnPagination.pageSize,
        txnSearch,
        txnPaymentType,
        txnPaymentMode
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const financeColumns = [
    {
      title: "Payment by",
      dataIndex: "payment_by",
      key: "payment_by",
    },
    {
      title: "Financier Name",
      dataIndex: "financier_name",
      key: "financier_name",
    },
    {
      title: "In House",
      dataIndex: "in_house",
      key: "in_house",
    },
    {
      title: "File Stage",
      dataIndex: "file_stage",
      key: "file_stage",
    },
    {
      title: "Attachment",
      dataIndex: "documents",
      key: "attachment",
      width: 200,
      render: (documents) => {
        if (!documents || documents.length === 0) {
          return "-";
        }
        const maxVisible = 2;
        const visibleDocs = documents.slice(0, maxVisible);
        const remainingCount = documents.length - maxVisible;

        return (
          <div
            className="d-flex flex-column align-items-start"
            style={{ gap: "4px", maxWidth: "200px" }}
          >
            {visibleDocs.map((doc, index) => (
              <Tooltip
                key={doc.id || index}
                title={doc.document_name || `Attachment ${index + 1}`}
              >
                <a
                  href={doc.document_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "2px 6px",
                    textDecoration: "none",
                    color: "#1890ff",
                    fontSize: "12px",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: "1.4",
                  }}
                >
                  {doc.document_name || `Doc ${index + 1}`}
                </a>
              </Tooltip>
            ))}
            {remainingCount > 0 && (
              <Tooltip
                title={
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {documents.slice(maxVisible).map((doc, index) => (
                      <div
                        key={doc.id || index + maxVisible}
                        style={{ marginBottom: "4px" }}
                      >
                        <a
                          href={doc.document_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#fff", textDecoration: "underline" }}
                        >
                          {doc.document_name ||
                            `Attachment ${index + maxVisible + 1}`}
                        </a>
                      </div>
                    ))}
                  </div>
                }
              >
                <Tag color="blue" style={{ cursor: "pointer", margin: 0 }}>
                  +{remainingCount} more
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (text) => renderRemarks(text),
      width: 200,

    },
    {
      title: "Last Updated",
      dataIndex: "last_updated",
      key: "last_updated",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        // Hide all actions for view-only roles
        if (isViewOnlyRole) {
          return "-";
        }
        return (
          <Space>
            {isLeadOpen && (
              <>
                <Button
                  type="primary"
                  size="small"
                  className="secondary-button"
                  onClick={async () => {
                    try {
                      const res = await getLeadFinanceInformationByIdService(
                        record.id
                      );
                      const data = res?.data?.data || {};
                      setIsEditFinance(true);
                      setEditingFinanceId(record.id);
                      financeForm.setFieldsValue({
                        payment_by: data.payment_by,
                        bank_id: data.bank_id || undefined,
                        file_stage_id: data.file_stage_id || undefined,
                        in_house: data.in_house || undefined,
                        remarks: data.remarks || "",
                      });
                      // Map existing documents to Upload fileList
                      const existingFiles =
                        (data.documents || []).map((doc) => ({
                          uid: doc.id,
                          name: doc.document_name,
                          status: "done",
                          url: doc.document_path,
                        })) || [];
                      setFinanceFiles(existingFiles);
                      setIsFinanceModalOpen(true);
                    } catch (error) {
                      notification.error({
                        description:
                          error?.response?.data?.message || error?.message,
                      });
                    }
                  }}
                >
                  Edit
                </Button>
                <Button
                  ghost
                  danger
                  className="primary-button"
                  size="small"
                  onClick={async () => {
                    Modal.confirm({
                      title: "Delete Finance Information",
                      content: "Are you sure you want to delete this record?",
                      okText: "Delete",
                      okType: "primary",
                      cancelText: "Cancel",
                      okButtonProps: { className: "secondary-button" },
                      cancelButtonProps: {
                        ghost: true,
                        danger: true,
                        className: "primary-button",
                      },
                      onOk: async () => {
                        try {
                          const deleteFinanceRes = await deleteLeadFinanceInformationService(record.id);
                          notification.success({
                            message: deleteFinanceRes?.data?.message || "Finance information deleted successfully",
                          });
                          fetchFinanceInformation(
                            financePagination.current,
                            financePagination.pageSize,
                            financeSearch
                          );
                          // Fetch lead details after successful delete
                          dispatch(fetchLeadDetailsServices({ id: leadId }));
                        } catch (error) {
                          notification.error({
                            description:
                              error?.response?.data?.message ?? error?.message,
                          });
                        }
                      },
                    });
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  // Helper function to render remarks with tooltip and "See more"
  const formatTransactionModeLabel = (raw) => {
    if (raw == null || raw === "") return "";
    const s = String(raw).trim().toLowerCase();
    if (s === "normal" || s === "normal entry") return "Normal Entry";
    if (s === "reversal" || s === "reversal entry") return "Reversal Entry";
    return String(raw);
  };

  const renderRemarks = (text) => {
    const displayText = text || "-";
    if (!displayText || displayText === "-") return "-";

    const maxLength = 50;
    const isLongText = displayText.length > maxLength;
    const truncatedText = isLongText ? `${displayText.substring(0, maxLength)}...` : displayText;

    return (
      <div style={{ wordBreak: "break-word" }}>
        <Tooltip title={displayText} placement="topLeft">
          <span>{truncatedText}</span>
        </Tooltip>
        {isLongText && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, marginLeft: 8, height: "auto" }}
            onClick={() => {
              setSelectedRemarks(displayText);
              setRemarksModalVisible(true);
            }}
          >
            See more
          </Button>
        )}
      </div>
    );
  };

  const transactionColumns = [
    {
      title: "Payment For",
      dataIndex: "payment_for",
      key: "payment_for",
      render: (text) => <Tag color="blue">{text || "-"}</Tag>,
    },
    {
      title: "Transaction Mode",
      dataIndex: "transaction_mode",
      key: "transaction_mode",
      render: (text) => formatTransactionModeLabel(text) || "-",
    },
    {
      title: "Transaction Nature",
      dataIndex: "transaction_nature",
      key: "transaction_nature",
      render: (text) => formatTransactionModeLabel(text) || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Payment Mode",
      dataIndex: "payment_mode",
      key: "payment_mode",
    },
    {
      title: "MR / UTR Number",
      dataIndex: "mr_utr_number",
      key: "mr_utr_number",
    },
    {
      title: "Paid on",
      dataIndex: "paid_on",
      key: "paid_on",
    },
    {
      title: "Attachment",
      dataIndex: "attachment",
      key: "attachment",
      render: (text, record) =>
        record?.attachment_path ? (
          <a
            href={record.attachment_path}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text || "View"}
          </a>
        ) : (
          text || "-"
        ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      render: (text) => renderRemarks(text),
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
    },
    {
      title: "Running Balance",
      dataIndex: "running_balance",
      key: "running_balance",
    },
  ];

  const handleTransactionSave = async (values) => {
    if (isTransactionSaveLoading) return;
    setIsTransactionSaveLoading(true);
    try {
      const formData = new FormData();
      formData.append("lead_id", leadId);
      if (isEditTransaction && editingTransactionId) {
        formData.append("id", editingTransactionId);
      }
      formData.append("payment_type", values.payment_for);
      if (values.transaction_mode) {
        formData.append("transaction_mode", values.transaction_mode);
      }
      if (values.reverses_transaction_id != null && values.reverses_transaction_id !== "") {
        formData.append("reverses_transaction_id", values.reverses_transaction_id);
      }
      formData.append("amount", values.amount);
      formData.append("payment_mode", values.payment_mode);
      formData.append(
        "payment_date",
        values.date_of_payment.format("YYYY-MM-DD")
      );
      if (values.mr_utr_number) {
        formData.append("mr_utr_number", values.mr_utr_number);
      }
      if (values.remarks) {
        formData.append("remarks", values.remarks);
      }
      // Reversal entry attachment handling:
      // - default file: send URL path
      // - newly selected file: send binary
      // - deleted file: send empty string
      if (values.transaction_mode === "reversal") {
        if (transactionFile?.length > 0) {
          const fileToUpload = transactionFile.find((f) => f.originFileObj);
          if (fileToUpload?.originFileObj) {
            formData.append("document", fileToUpload.originFileObj);
          } else if (transactionFile?.[0]?.url) {
            formData.append("document", transactionFile[0].url);
          }
        } else {
          formData.append("document", "");
        }
      } else if (transactionFile?.length > 0) {
        // Existing behavior for non-reversal entries.
        const fileToUpload = transactionFile.find((f) => f.originFileObj);
        if (fileToUpload?.originFileObj) {
          formData.append("document", fileToUpload.originFileObj);
        }
      }
      let res;
      if (isEditTransaction && editingTransactionId) {
        res = await updatePaymentTransactionService(formData);
      } else {
        res = await addPaymentTransactionService(formData);
      }

      // Display dynamic message from response
      const successMessage = res?.data?.message || 
                            (isEditTransaction ? "Transaction updated successfully" : "Transaction saved successfully");
      
      // Check if refund_amount_recommended exists in response data
      if (res?.data?.data?.refund_amount_recommended !== undefined && 
          res?.data?.data?.refund_amount_recommended !== null) {
        notification.warning({
          message: successMessage,
        });
      } else {
        notification.success({
          message: successMessage,
        });
      }
      setIsTransactionModalOpen(false);
      form.resetFields();
      setTransactionFile([]);
      setIsEditTransaction(false);
      setEditingTransactionId(null);
      fetchTransactions(
        1,
        txnPagination.pageSize,
        txnSearch,
        txnPaymentType,
        txnPaymentMode
      );
      // Fetch lead details after successful save (updates Total Payment Received & Net Customer Balance)
      dispatch(fetchLeadDetailsServices({ id: leadId }));
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setIsTransactionSaveLoading(false);
    }
  };

  const handleFinanceSave = async (values) => {
    if (isFinanceSaveLoading) return;
    setIsFinanceSaveLoading(true);
    try {
      const formData = new FormData();
      formData.append("lead_id", leadId);
      if (isEditFinance && editingFinanceId) {
        formData.append("id", editingFinanceId);
      }
      formData.append("payment_by", values.payment_by);
      // Append optional fields if provided
      if (values.bank_id !== undefined && values.bank_id !== null)
        formData.append("bank_id", values.bank_id);
      if (values.file_stage_id !== undefined && values.file_stage_id !== null)
        formData.append("file_stage_id", values.file_stage_id);
      if (values.in_house !== undefined && values.in_house !== null)
        formData.append("in_house", values.in_house);
      if (values.remarks) formData.append("remarks", values.remarks);
      // documents[] - append all selected files
      if (financeFiles?.length > 0) {
        let updateIdx = 0;
        financeFiles.forEach((file) => {
          if (file.originFileObj) {
            // If existing file (has url and numeric uid), send in update_documents
            const isExisting =
              file.url && file.uid && typeof file.uid === "number";
            if (isEditFinance && isExisting) {
              formData.append(
                `update_documents[${updateIdx}][document_id]`,
                file.uid
              );
              formData.append(
                `update_documents[${updateIdx}][document]`,
                file.originFileObj
              );
              updateIdx += 1;
            } else {
              formData.append("documents[]", file.originFileObj);
            }
          }
        });
      }

      let financeRes;
      if (isEditFinance && editingFinanceId) {
        financeRes = await updateLeadFinanceInformationService(formData);
      } else {
        financeRes = await addLeadFinanceInformationService(formData);
      }
      
      // Display dynamic message from response
      const financeMessage = financeRes?.data?.message || 
                            (isEditFinance ? "Finance information updated successfully" : "Finance information saved successfully");
      notification.success({
        message: financeMessage,
      });
      setIsFinanceModalOpen(false);
      financeForm.resetFields();
      setFinanceFiles([]);
      setIsEditFinance(false);
      setEditingFinanceId(null);
      fetchFinanceInformation(1, financePagination.pageSize, financeSearch);
      // Fetch lead details after successful save
      dispatch(fetchLeadDetailsServices({ id: leadId }));
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setIsFinanceSaveLoading(false);
    }
  };

  const handleTransactionFileChange = (info) => {
    setTransactionFile([...info.fileList]);
  };

  const validateTransactionFile = (file) => {
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "application/pdf";
    if (!isValidType) {
      notification.error({
        description: "You can only upload JPG/PNG/GIF/PDF file",
      });
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      notification.error({
        description: "File must be smaller than 5MB",
      });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleFinanceFileChange = (info) => {
    setFinanceFiles([...info.fileList]);
  };

  const validateFile = (file) => {
    const isValidType =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "application/pdf";
    if (!isValidType) {
      notification.error({
        description: "You can only upload JPG/PNG/GIF/PDF file",
      });
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notification.error({
        description: "File must be smaller than 10MB",
      });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // Combined loading state for the main loader
  const isLoading = financeLoading || txnLoading;

  return (
    <>
      <Loader loading={isLoading} />
      <div>
        <Flex vertical gap={24} className="personal-detail">
          {/* Finance Information Section */}

          <Card>
            <Title level={5} style={{ marginBottom: "15px" }}>
              Finance Information
            </Title>
            <Flex vertical gap={16}>
              <Flex gap={16} align="center" justify="space-between">
                <Input.Search
                  placeholder="Search finance info"
                  allowClear
                  value={financeSearch}
                  onChange={(e) => setFinanceSearch(e.target.value)}
                  onSearch={handleFinanceSearchSubmit}
                  onPressEnter={(e) => handleFinanceSearchSubmit(e.target.value)}
                  style={{ width: 260 }}
                  className="form-control-ant"
                />
                {!isViewOnlyRole && isLeadOpen && (
                  <Button
                    type="primary"
                    onClick={() => {
                      setIsEditFinance(false);
                      setIsFinanceModalOpen(true);
                    }}
                    className="secondary-button"
                  >
                    New
                  </Button>
                )}
              </Flex>

              <Table
                columns={financeColumns}
                dataSource={financeData}
                loading={financeLoading}
                pagination={{
                  current: financePagination.current,
                  pageSize: financePagination.pageSize,
                  total: financePagination.total,
                  showSizeChanger: true,
                }}
                onChange={(pagination) => {
                  const { current, pageSize } = pagination;
                  fetchFinanceInformation(current, pageSize, financeSearch);
                }}
                locale={{ emptyText: "No data" }}
                scroll={{ x: "max-content" }} // 👈 makes the table responsive
              />
            </Flex>
          </Card>
          {/* Transactions Section */}

          <Card>
            <Title level={5} style={{ marginBottom: "15px" }}>
              Transactions
            </Title>
            <Flex vertical gap={16}>
              <Flex
                gap={16}
                align="center"
                justify="space-between"
                className="flex-wrap"
              >
                <Flex gap={16} align="center" className="flex-wrap">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={8}>
                      <Input.Search
                        placeholder="Search MR/UTR number"
                        allowClear
                        value={txnSearch}
                        onChange={(e) => setTxnSearch(e.target.value)}
                        onSearch={(val) =>
                          fetchTransactions(
                            1,
                            txnPagination.pageSize,
                            val?.trim(),
                            txnPaymentType,
                            txnPaymentMode
                          )
                        }
                        onPressEnter={(e) =>
                          fetchTransactions(
                            1,
                            txnPagination.pageSize,
                            e.target.value?.trim(),
                            txnPaymentType,
                            txnPaymentMode
                          )
                        }
                        className="form-control-ant"
                      />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={8}>
                      <Select
                        placeholder="Payment For"
                        value={txnPaymentType}
                        onChange={(val) => {
                          setTxnPaymentType(val);
                          fetchTransactions(
                            1,
                            txnPagination.pageSize,
                            txnSearch,
                            val === undefined ? "" : val,
                            txnPaymentMode
                          );
                        }}
                        className="form-control-ant"
                        allowClear
                      >
                        {[
                          "Booking",
                          "Down Payment",
                          "Delivery Order",
                          "Disbursement",
                        ].map((opt) => (
                          <Select.Option key={opt} value={opt}>
                            {opt}
                          </Select.Option>
                        ))}
                        <Select.Option key="Refund" value="Refund">
                          Refund
                        </Select.Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={8}>
                      <Select
                        placeholder="Payment Mode"
                        value={txnPaymentMode}
                        onChange={(val) => {
                          setTxnPaymentMode(val);
                          fetchTransactions(
                            1,
                            txnPagination.pageSize,
                            txnSearch,
                            txnPaymentType,
                            val === undefined ? "" : val
                          );
                        }}
                        className="form-control-ant"
                        allowClear
                      >
                        {["Cash", "UPI", "IMPS", "NEFT", "RTGS", "Cheque"].map(
                          (opt) => (
                            <Select.Option key={opt} value={opt}>
                              {opt}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Col>
                  </Row>
                </Flex>

                {!isViewOnlyRole &&
                  (isLeadOpen ||
                    ((leadDetails?.lead_status?.name === "Converted" ||
                      isLeadRefundedOrCancelled) &&
                      leadDetails?.refund_amount_recommended !== 0)) && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsEditTransaction(false);
                        setEditingTransactionId(null);
                        setTransactionFile([]);
                        form.resetFields();
                        form.setFieldsValue({ date_of_payment: dayjs() });
                        setIsTransactionModalOpen(true);
                      }}
                      className="secondary-button"
                    >
                      New
                    </Button>
                  )}
              </Flex>

              <Table
                columns={transactionColumns}
                dataSource={txnData}
                loading={txnLoading}
                pagination={{
                  current: txnPagination.current,
                  pageSize: txnPagination.pageSize,
                  total: txnPagination.total,
                  showSizeChanger: true,
                }}
                onChange={(pagination) => {
                  const { current, pageSize } = pagination;
                  fetchTransactions(
                    current,
                    pageSize,
                    txnSearch,
                    txnPaymentType,
                    txnPaymentMode
                  );
                }}
                locale={{ emptyText: "No data" }}
                scroll={{ x: "max-content" }} // 👈 makes the table responsive
              />
              {txnFinalBalance != null && (
                <Flex align="center" justify="flex-end" style={{ marginTop: 16 }}>
                  <strong>Final Balance: </strong>
                  <span style={{ marginLeft: 8 }}>
                    ₹{Number(txnFinalBalance).toLocaleString("en-IN")}
                  </span>
                </Flex>
              )}
            </Flex>
          </Card>
          {/* <Card title="Balance Information" variant="borderless"> */}
          <Row gutter={[16, 16]} justify="end">
            <Col xs={24} sm={24} lg={8}>
              <Flex vertical gap={16}>
                <Flex align="center" justify="space-between">
                  <strong>Deal Value</strong>
                  <span>
                    {bookingData?.total_customer_payable !== null &&
                      bookingData?.total_customer_payable !== undefined
                      ? `₹${bookingData.total_customer_payable.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </Flex>
                <Flex align="center" justify="space-between">
                  <strong>Total Payment Received</strong>
                  <span>
                    {totalPaymentReceived !== null &&
                      totalPaymentReceived !== undefined
                      ? `₹${totalPaymentReceived.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </Flex>
                <Flex align="center" justify="space-between">
                  <strong>Cash Amount</strong>
                  <span>
                    {displayCashAmount !== null && displayCashAmount !== undefined
                      ? `₹${displayCashAmount.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </Flex>
                <Flex align="center" justify="space-between">
                  <strong>Recommended Refund Amount</strong>
                  <span>
                    {leadDetails?.refund_amount_recommended != null &&
                    leadDetails?.refund_amount_recommended !== undefined
                      ? `₹${Number(
                          leadDetails.refund_amount_recommended
                        ).toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </Flex>
                <Flex align="center" justify="space-between">
                  <strong>Net Customer Balance</strong>
                  <span>
                    {netCusBalance !== null && netCusBalance !== undefined
                      ? `₹${netCusBalance.toLocaleString("en-IN")}`
                      : "-"}
                  </span>
                </Flex>
              </Flex>
            </Col>

            {isLeadRefundedOrCancelled && (
              <>
                <Col xs={24} sm={24} lg={8} style={{ color: "red" }}>
                  <Flex vertical gap={16}>
                    <Flex align="center" justify="space-between">
                      <strong>Refunded</strong>
                      <span>
                        {refundAmount !== null && refundAmount !== undefined
                          ? `₹${refundAmount.toLocaleString("en-IN")}`
                          : "-"}
                      </span>
                    </Flex>
                    <Flex align="center" justify="space-between">
                      <strong>Cash Amount</strong>
                      <span>
                        {bookingData?.refund_cash_amount !== null &&
                          bookingData?.refund_cash_amount !== undefined
                          ? `₹${bookingData.refund_cash_amount.toLocaleString(
                            "en-IN"
                          )}`
                          : "-"}
                      </span>
                    </Flex>
                  </Flex>
                </Col>
              </>
            )}
          </Row>
          {/* </Card> */}
        </Flex>

        {/* Transaction Modal */}
        <TransactionModal
          leadId={leadId}
          open={isTransactionModalOpen}
          onCancel={() => {
            if (isTransactionSaveLoading) return;
            setIsTransactionModalOpen(false);
            form.resetFields();
            setTransactionFile([]);
            setIsEditTransaction(false);
            setEditingTransactionId(null);
          }}
          form={form}
          onFinish={handleTransactionSave}
          isEditTransaction={isEditTransaction}
          isLeadOpen={isLeadOpen}
          isRefundEnabled={isRefundEnabled}
          saveLoading={isTransactionSaveLoading}
          onlyBookingAllowed={isBookingAmountZero}
          transactionFile={transactionFile}
          onTransactionFileChange={handleTransactionFileChange}
          validateTransactionFile={validateTransactionFile}
          // fixedPaymentType="Payment"

        />

        {/* Finance Information Modal */}
        <Modal
          title={
            <div>
              <h5
                className=" mb-0"
                style={{ fontSize: "20px", fontWeight: "500" }}
              >
                {isEditFinance ? "Update Finance Info." : "Add Finance Info."}
              </h5>
              <hr style={{ margin: "15px 0 0 0" }} />
            </div>
          }
          open={isFinanceModalOpen}
          onCancel={() => {
            if (isFinanceSaveLoading) return;
            setIsFinanceModalOpen(false);
            financeForm.resetFields();
            setFinanceFiles([]);
            setIsEditFinance(false);
          }}
          closable={!isFinanceSaveLoading}
          maskClosable={false}
          width={800}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                if (isFinanceSaveLoading) return;
                setIsFinanceModalOpen(false);
                financeForm.resetFields();
                setFinanceFiles([]);
                setIsEditFinance(false);
              }}
              ghost
              danger
              className="primary-button"
              disabled={isFinanceSaveLoading}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              className="secondary-button"
              loading={isFinanceSaveLoading}
              disabled={isFinanceSaveLoading}
              onClick={() => financeForm.submit()}
            >
              Save
            </Button>,
          ]}
        >
          <Form form={financeForm} layout="vertical" onFinish={handleFinanceSave}>
            <Row gutter={[16, 16]}>
              <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                <Form.Item
                  name="payment_by"
                  label="Payment by"
                  rules={[
                    { required: true, message: "Please select payment by" },
                  ]}
                >
                  <Select
                    placeholder="Select One"
                    className="form-control-ant"
                    onChange={(val) => {
                      if (val === "Cash") {
                        financeForm.setFieldsValue({
                          bank_id: undefined,
                          file_stage_id: undefined,
                          in_house: undefined,
                        });
                      }
                    }}
                  >
                    <Select.Option value="Finance">Finance</Select.Option>
                    <Select.Option value="Cash">Cash</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              {paymentBy !== "Cash" && (
                <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    name="bank_id"
                    label="Financier Name"
                    rules={[
                      { required: true, message: "Please select financier name" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            getFieldValue("payment_by") === "Finance" &&
                            !value
                          ) {
                            return Promise.reject("Please select financier name");
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Select
                      placeholder="Select One"
                      className="form-control-ant"
                      showSearch
                      optionFilterProp="children"
                    >
                      {bankOptions?.map((bank) => (
                        <Select.Option key={bank.id} value={bank.id}>
                          {bank.name || bank.title || bank.bank_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}

              {paymentBy !== "Cash" && (
                <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    name="in_house"
                    label="In House"
                    rules={[
                      { required: true, message: "Please select in house" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            getFieldValue("payment_by") === "Finance" &&
                            !value
                          ) {
                            return Promise.reject("Please select in house");
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Select placeholder="Select One" className="form-control-ant">
                      <Select.Option value="Yes">Yes</Select.Option>
                      <Select.Option value="No">No</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}

              {paymentBy !== "Cash" && (
                <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    name="file_stage_id"
                    label="File Stage"
                    rules={[
                      { required: true, message: "Please select file stage" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            getFieldValue("payment_by") === "Finance" &&
                            !value
                          ) {
                            return Promise.reject("Please select file stage");
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Select
                      placeholder="Select One"
                      className="form-control-ant"
                      showSearch
                      optionFilterProp="children"
                    >
                      {fileStages?.map((stage) => (
                        <Select.Option key={stage.id} value={stage.id}>
                          {stage.stage_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}

              <Col xl={12} lg={12} md={12} sm={24} xs={24}>
                <Form.Item
                  name="attachment"
                  label="Attachment"
                  rules={[
                    {
                      required: false,
                      message: "Please upload attachment",
                    },
                  ]}
                >
                  <Upload
                    multiple
                    maxCount={5}
                    beforeUpload={validateFile}
                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                    customRequest={({ file, onSuccess }) => {
                      console.log(file);
                      onSuccess("ok");
                    }}
                    onChange={handleFinanceFileChange}
                    fileList={financeFiles}
                    showUploadList={{ showRemoveIcon: true }}
                  >
                    <Button icon={<UploadOutlined />}></Button>
                  </Upload>
                </Form.Item>
              </Col>

              <Col xl={24} lg={24} md={24} sm={24} xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <TextArea rows={4} placeholder="Enter Remarks" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Delete Transaction Modal */}
        <Modal
          title={
            <div>
              <h5
                className=" mb-0"
                style={{ fontSize: "20px", fontWeight: "500" }}
              >
                Delete Transaction
              </h5>
              <hr style={{ margin: "15px 0 0 0" }} />
            </div>
          }
          open={isDeleteTransactionModalOpen}
          onCancel={() => {
            setIsDeleteTransactionModalOpen(false);
            deleteTransactionForm.resetFields();
            setDeleteTransactionId(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setIsDeleteTransactionModalOpen(false);
                deleteTransactionForm.resetFields();
                setDeleteTransactionId(null);
              }}
              ghost
              danger
              className="primary-button"
            >
              Cancel
            </Button>,
            <Button
              key="delete"
              type="primary"
              danger
              className="secondary-button"
              onClick={() => deleteTransactionForm.submit()}
            >
              Delete
            </Button>,
          ]}
          maskClosable={false}
          width={500}
        >
          <Form
            form={deleteTransactionForm}
            layout="vertical"
            onFinish={async (values) => {
              try {
                const deleteTransactionRes = await deletePaymentTransactionService(
                  deleteTransactionId,
                  values.remarks
                );
                notification.success({
                  message: deleteTransactionRes?.data?.message || "Transaction deleted successfully",
                });
                fetchTransactions(
                  txnPagination.current,
                  txnPagination.pageSize,
                  txnSearch,
                  txnPaymentType,
                  txnPaymentMode
                );
                dispatch(fetchLeadDetailsServices({ id: leadId }));
                setIsDeleteTransactionModalOpen(false);
                deleteTransactionForm.resetFields();
                setDeleteTransactionId(null);
              } catch (error) {
                notification.error({
                  description: error?.response?.data?.message ?? error?.message,
                });
              }
            }}
          >
            {/* <p>Are you sure you want to delete this transaction?</p> */}
            <Form.Item
              name="remarks"
              label="Remarks"
              rules={[
                { required: true, message: "Please enter remarks" },
                {
                  max: 200,
                  message: "Remarks cannot exceed 200 characters",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter remarks"
                maxLength={200}
                showCount
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Remarks Modal */}
        <Modal
          title="Remarks"
          open={remarksModalVisible}
          onCancel={() => {
            setRemarksModalVisible(false);
            setSelectedRemarks("");
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setRemarksModalVisible(false);
                setSelectedRemarks("");
              }}
            >
              Close
            </Button>,
          ]}
          width={600}
        >
          <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {selectedRemarks}
          </p>
        </Modal>
      </div>
    </>
  );
};

export default Payments;
